/**
 * The Wordle screen.
 *
 * One component tree, no router: a settings row, some number of grids, and a
 * keyboard. Everything the game needs — the answers, the guesses, the stats —
 * lives in this component and is mirrored to localStorage on every change, so a
 * reload mid-game puts the player back exactly where they were, with no network
 * anywhere in the path.
 *
 * The multi-board variants are not a different game, they are the same `Game`
 * with more than one answer in it. That is worth knowing before reading the
 * layout below: there is one guess row being typed, one keyboard, one history,
 * and the boards are views onto it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';

import {
  boardRows,
  keyStates,
  refuse,
  shareText,
  solvedAt,
  status,
  totalGuesses,
  type Game,
} from './game';
import { isLegalGuess, pickAnswers } from './words';
import { load, record, save, statsKey, EMPTY_STAT, type Settings, type Stat } from './storage';
import {
  BOARD_COUNTS,
  BOARD_LABEL,
  WORD_LENGTHS,
  type BoardCount,
  type KeyState,
  type TileState,
  type WordLength,
} from './types';

const KEY_ROWS = ['qwertyuiop', 'asdfghjkl', 'zxcvbnm'] as const;

/** How long a refusal ("not in the word list") stays on screen. */
const NOTE_MS = 1800;

/** Must outlast the `wd-shake` animation in the stylesheet. */
const SHAKE_MS = 450;

// ── Grid ────────────────────────────────────────────────────────────────────

interface TileProps {
  letter: string;
  state: TileState;
  /** Typed but not submitted: a letter with no verdict behind it yet. */
  pending: boolean;
  /** Its place in the word, which is what staggers the winning row's hop. */
  column: number;
}

function Tile({ letter, state, pending, column }: TileProps): JSX.Element {
  const classes = ['wd-tile', `wd-${state}`];
  if (pending) classes.push('wd-pending');
  return (
    <div class={classes.join(' ')} style={`--wd-hop:${column}`}>
      {letter.toUpperCase()}
    </div>
  );
}

interface BoardProps {
  game: Game;
  board: number;
  /** The word being typed, shown on the first unplayed row of open boards. */
  typed: string;
  playing: boolean;
  /**
   * Bumped on every refused word. A counter rather than a flag because it is
   * also the row's key: re-running a CSS animation for a second bad word in a
   * row needs the element replaced, and a class that is already set does not do
   * that.
   */
  shake: number;
  /**
   * The guess just submitted, or null when nothing was — a reload, or a game
   * dealt fresh. A board whose winning row is that guess has just been solved
   * in front of the player, which is the only time it dances. Deliberately not
   * saved: coming back to a finished board is not the same event as finishing
   * one, and reading the answer off a reloaded grid should be quiet.
   */
  justPlayed: number | null;
}

/**
 * One word's grid.
 *
 * Rows are read out of `boardRows`, which already knows to stop scoring once
 * this board is solved — so a board that fell on guess three keeps its three
 * rows and sits there while the rest of the game plays on beneath it.
 */
function Board({ game, board, typed, playing, shake, justPlayed }: BoardProps): JSX.Element {
  const rows = boardRows(game, board);
  const done = solvedAt(game, board);
  const total = totalGuesses(game);
  const answer = game.answers[board] ?? '';
  const open = done === null;

  const cells = [];
  for (let row = 0; row < total; row += 1) {
    const played = row < game.guesses.length && (done === null || row <= done);
    const typing = open && playing && row === game.guesses.length;
    const letters = played ? (game.guesses[row] ?? '') : typing ? typed : '';
    const states = rows[row];
    // The row that just won this board, and only on the guess that won it.
    const dancing = done !== null && row === done && done === justPlayed;
    cells.push(
      <div
        class={`wd-row${typing && shake > 0 ? ' wd-shake' : ''}${dancing ? ' wd-dance' : ''}`}
        key={typing ? `typing-${shake}` : row}
      >
        {Array.from({ length: game.length }, (_, column) => (
          <Tile
            key={column}
            letter={letters[column] ?? ''}
            state={(played ? states?.[column] : undefined) ?? 'empty'}
            pending={typing && column < letters.length}
            column={column}
          />
        ))}
      </div>,
    );
  }

  // The word itself only ever appears once the game is over and this board was
  // not solved. Anywhere else it would be sitting in the DOM of a game in
  // progress, one devtools panel away from the answer.
  const revealed = !playing && open ? answer.toUpperCase() : null;

  return (
    <div class={`wd-board${open ? '' : ' wd-board-done'}`} aria-label={`Word ${board + 1}`}>
      {game.boards > 1 && (
        <div class="wd-board-head">
          <span class="wd-board-name">Word {board + 1}</span>
          {done !== null && <span class="wd-board-score">✓ {done + 1}</span>}
        </div>
      )}
      <div class="wd-grid">{cells}</div>
      {revealed && <div class="wd-reveal">{revealed}</div>}
    </div>
  );
}

// ── Keyboard ────────────────────────────────────────────────────────────────

interface KeyProps {
  letter: string;
  states: KeyState[];
  onPress: (letter: string) => void;
}

/**
 * One letter key, divided once per board.
 *
 * With four words in play a key cannot have a single colour — E can be green in
 * one word and eliminated in another, and flattening that to one verdict throws
 * away the more useful half of it. So the key is divided into one patch per
 * board, in board order, and the stylesheet arranges those patches the way the
 * boards themselves are arranged: stripes for one or two, quadrants for four.
 * Board order fills either the same way, so the markup is the same both times.
 *
 * With one board the patch fills the key and this is the ordinary coloured
 * keyboard everyone already knows.
 */
function Key({ letter, states, onPress }: KeyProps): JSX.Element {
  const known = states.some((state) => state !== 'empty');
  return (
    <button
      type="button"
      class={`wd-key${known ? ' wd-key-known' : ''}`}
      // Without this the key keeps focus, and the next physical Enter — meant
      // for the guess — replays the click instead.
      onClick={(event) => {
        (event.currentTarget as HTMLButtonElement).blur();
        onPress(letter);
      }}
    >
      <span class="wd-patches" aria-hidden="true">
        {states.map((state, board) => (
          <span key={board} class={`wd-patch wd-s-${state}`} />
        ))}
      </span>
      <span class="wd-key-letter">{letter.toUpperCase()}</span>
    </button>
  );
}

interface KeyboardProps {
  game: Game;
  onLetter: (letter: string) => void;
  onEnter: () => void;
  onBack: () => void;
}

function Keyboard({ game, onLetter, onEnter, onBack }: KeyboardProps): JSX.Element {
  const states = useMemo(() => keyStates(game), [game]);
  const blank = useMemo<KeyState[]>(
    () => Array.from({ length: game.boards }, () => 'empty' as KeyState),
    [game.boards],
  );

  return (
    <div class="wd-keys" data-boards={game.boards}>
      {KEY_ROWS.map((row, index) => (
        <div class="wd-krow" key={row}>
          {index === 2 && (
            <button
              type="button"
              class="wd-key wd-key-wide"
              onClick={(event) => {
                (event.currentTarget as HTMLButtonElement).blur();
                onEnter();
              }}
            >
              <span class="wd-key-letter">Enter</span>
            </button>
          )}
          {[...row].map((letter) => (
            <Key
              key={letter}
              letter={letter}
              states={states.get(letter) ?? blank}
              onPress={onLetter}
            />
          ))}
          {index === 2 && (
            <button
              type="button"
              class="wd-key wd-key-wide"
              aria-label="Delete"
              onClick={(event) => {
                (event.currentTarget as HTMLButtonElement).blur();
                onBack();
              }}
            >
              <span class="wd-key-letter">⌫</span>
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Controls ────────────────────────────────────────────────────────────────

interface ChoiceProps<T extends number> {
  label: string;
  options: readonly T[];
  value: T;
  name: (option: T) => string;
  /** The long form, for the tooltip and the accessible name. */
  title: (option: T) => string;
  onPick: (option: T) => void;
}

/**
 * A segmented picker, label and all on one line.
 *
 * Buttons rather than a `<select>`: there are three options and both settings
 * are the kind a player flicks between, so hiding them behind a menu costs a tap
 * each time to save nothing. The label sits beside the buttons rather than above
 * them because a second line here is a row of tiles lost below.
 *
 * The buttons read as bare numbers, so each carries its long form as its
 * accessible name — "4" alone is not a control anyone can identify.
 */
function Choice<T extends number>({
  label,
  options,
  value,
  name,
  title,
  onPick,
}: ChoiceProps<T>): JSX.Element {
  return (
    <div class="wd-choice">
      <span class="wd-choice-label">{label}</span>
      <div class="wd-segments" role="group" aria-label={label}>
        {options.map((option) => (
          <button
            key={option}
            type="button"
            class={`wd-segment${option === value ? ' wd-segment-on' : ''}`}
            aria-pressed={option === value}
            aria-label={title(option)}
            title={title(option)}
            onClick={(event) => {
              (event.currentTarget as HTMLButtonElement).blur();
              onPick(option);
            }}
          >
            {name(option)}
          </button>
        ))}
      </div>
    </div>
  );
}

function StatsRow({
  stat,
  boards,
  length,
}: {
  stat: Stat;
  boards: BoardCount;
  length: WordLength;
}): JSX.Element {
  const rate = stat.played === 0 ? 0 : Math.round((stat.won / stat.played) * 100);
  return (
    <dl class="wd-stats">
      <div>
        <dt>
          {boards} × {length}
        </dt>
        <dd>{stat.played} played</dd>
      </div>
      <div>
        <dt>Won</dt>
        <dd>{rate}%</dd>
      </div>
      <div>
        <dt>Streak</dt>
        <dd>{stat.streak}</dd>
      </div>
      <div>
        <dt>Best</dt>
        <dd>{stat.best}</dd>
      </div>
    </dl>
  );
}

// ── Screen ──────────────────────────────────────────────────────────────────

export function App(): JSX.Element {
  const initial = useMemo(() => load(), []);

  // The saved game wins over the saved preference when the two disagree. They
  // can: the settings are written the moment they are picked, and a game that
  // was in progress at the time is still a 5-letter game.
  const [boards, setBoards] = useState<BoardCount>(initial.game?.boards ?? initial.boards);
  const [length, setLength] = useState<WordLength>(initial.game?.length ?? initial.length);
  const [settings, setSettings] = useState<Settings>(initial.settings);
  const [game, setGame] = useState<Game | null>(initial.game);
  const [stats, setStats] = useState(initial.stats);
  const [typed, setTyped] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const [shake, setShake] = useState(0);
  const [copied, setCopied] = useState(false);
  // Which guess the player has just played, or null if they have not played one
  // — a reload, or a game dealt fresh. Everything that celebrates hangs off
  // this, and it is deliberately not saved: a game restored from storage comes
  // back finished but still. See `justPlayed` on `BoardProps`.
  const [justPlayed, setJustPlayed] = useState<number | null>(null);

  const outcome = game ? status(game) : 'playing';
  const playing = game !== null && outcome === 'playing';
  const key = statsKey(boards, length);
  const stat = stats[key] ?? EMPTY_STAT;

  useEffect(() => {
    save({ boards, length, settings, game, stats });
  }, [boards, length, settings, game, stats]);

  useEffect(() => {
    if (note === null) return undefined;
    const timer = window.setTimeout(() => setNote(null), NOTE_MS);
    return () => window.clearTimeout(timer);
  }, [note]);

  // Back to 0 once the animation has run, so the next refusal is a change again.
  useEffect(() => {
    if (shake === 0) return undefined;
    const timer = window.setTimeout(() => setShake(0), SHAKE_MS);
    return () => window.clearTimeout(timer);
  }, [shake]);

  const newGame = useCallback((nextBoards: BoardCount, nextLength: WordLength): void => {
    setGame({
      boards: nextBoards,
      length: nextLength,
      answers: pickAnswers(nextLength, nextBoards),
      guesses: [],
    });
    setTyped('');
    setNote(null);
    setCopied(false);
    setJustPlayed(null);
  }, []);

  // First visit with nothing saved: deal a game rather than showing an empty
  // frame with a button on it.
  useEffect(() => {
    if (!game) newGame(boards, length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Playing ──────────────────────────────────────────────────────────────

  /**
   * The live game and the live typed word, for the key handlers.
   *
   * The keyboard listener is attached from an effect, so its closure is whatever
   * the last effect run captured — and a key that arrives before that effect has
   * re-run is handled with the state of the render *before* it. That is not a
   * theoretical race: typing five letters and Enter as fast as a keyboard can
   * send them is a person playing normally, and it submitted a word missing its
   * last letters. Reading through refs makes the handlers independent of when
   * they were attached, which in turn lets them be attached exactly once.
   */
  const live = useRef({ game, typed });
  live.current = { game, typed };

  const submit = useCallback((): void => {
    const { game, typed } = live.current;
    if (!game || status(game) !== 'playing') return;
    const word = typed.toLowerCase();
    const why = refuse(game, word, isLegalGuess);
    if (why !== null) {
      setNote(
        why === 'short' ? 'Not enough letters' : `${word.toUpperCase()} is not in the word list`,
      );
      setShake((count) => count + 1);
      return;
    }
    const next: Game = { ...game, guesses: [...game.guesses, word] };
    const after = status(next);
    // Recorded here rather than in an effect watching the game: this is the one
    // place a game can end, so it is the one place that cannot double-count it.
    if (after !== 'playing') {
      const at = statsKey(next.boards, next.length);
      setStats((previous) => ({ ...previous, [at]: record(previous[at], after === 'won') }));
    }
    setGame(next);
    // The index of the guess just added, which is what the boards check against
    // their winning row. This is the one place a guess is ever played, so it is
    // the one place that can honestly say a board fell in front of the player.
    setJustPlayed(game.guesses.length);
    setTyped('');
    setNote(null);
  }, []);

  const addLetter = useCallback((letter: string): void => {
    const { game } = live.current;
    if (!game || status(game) !== 'playing') return;
    setTyped((current) => (current.length >= game.length ? current : current + letter));
  }, []);

  const backspace = useCallback((): void => {
    const { game } = live.current;
    if (!game || status(game) !== 'playing') return;
    setTyped((current) => current.slice(0, -1));
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      // A key press aimed at a control is that control's business. Without this,
      // Enter after picking "4 words" would re-deal instead of submitting, and
      // space would toggle whichever segment was last touched.
      const target = event.target;
      if (target instanceof HTMLElement && target.closest('button, select, a, input')) return;

      if (event.key === 'Enter') {
        event.preventDefault();
        submit();
      } else if (event.key === 'Backspace') {
        event.preventDefault();
        backspace();
      } else if (/^[a-zA-Z]$/.test(event.key)) {
        addLetter(event.key.toLowerCase());
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // All three read the game through `live`, so they never go stale and this
    // listener is attached once for the life of the page.
  }, [submit, backspace, addLetter]);

  // ── Settings ─────────────────────────────────────────────────────────────

  // Both pickers deal a fresh game. There is no way to change the shape of a
  // board mid-game that is not either a rebuild or a lie about the guesses
  // already spent, and the game is short enough that restarting it is cheap.
  const pickBoards = (next: BoardCount): void => {
    if (next === boards) return;
    setBoards(next);
    newGame(next, length);
  };

  const pickLength = (next: WordLength): void => {
    if (next === length) return;
    setLength(next);
    newGame(boards, next);
  };

  const share = useCallback((): void => {
    if (!game) return;
    // Absent over plain HTTP and in some embedded browsers, so it is checked
    // rather than assumed — a missing clipboard should say so, not throw.
    if (!navigator.clipboard) {
      setNote('Copying is not available in this browser');
      return;
    }
    void navigator.clipboard
      .writeText(shareText(game))
      .then(() => setCopied(true))
      .catch(() => setNote('Could not reach the clipboard'));
  }, [game]);

  // ── Render ───────────────────────────────────────────────────────────────

  if (!game) {
    return (
      <main class="wd-app">
        <p class="wd-boot">Dealing a game…</p>
      </main>
    );
  }

  const left = totalGuesses(game) - game.guesses.length;
  const unsolved = game.answers.filter((_, board) => solvedAt(game, board) === null).length;

  /*
   * Four rows: a title bar, the settings, the boards, and the keyboard. The
   * boards row is the only flexible one, and everything else is deliberately
   * short, because the whole screen is one viewport tall and does not scroll —
   * four boards of nine rows plus a keyboard has to fit, and every line of prose
   * above it comes out of the tiles.
   *
   * So there is no rules paragraph and no stats footer here. The rules are on
   * the launcher card that sent you here, and the stats are on the panel that
   * replaces the keyboard when the game ends, which is when anyone looks at
   * them.
   */
  return (
    <main class={`wd-app${settings.highContrast ? ' wd-hc' : ''}`}>
      <header class="wd-top">
        <a class="wd-back" href="/" aria-label="Back to games">
          ←
        </a>
        <h1>Wordle</h1>
        <span class="wd-tally" role="status">
          {playing ? (
            <>
              {left} left
              {game.boards > 1 && (
                <>
                  {' · '}
                  <span class="wd-tally-open">
                    {unsolved} of {game.boards} to go
                  </span>
                </>
              )}
            </>
          ) : (
            <span class={outcome === 'won' ? 'wd-good' : 'wd-bad'}>
              {outcome === 'won'
                ? `Solved in ${game.guesses.length}`
                : unsolved === 1
                  ? 'One got away'
                  : `${unsolved} got away`}
            </span>
          )}
        </span>
        <button
          type="button"
          class="wd-icon-button"
          title="New game"
          aria-label="New game"
          onClick={(event) => {
            (event.currentTarget as HTMLButtonElement).blur();
            newGame(boards, length);
          }}
        >
          ↻
        </button>
        <button
          type="button"
          class={`wd-icon-button${settings.highContrast ? ' wd-icon-on' : ''}`}
          title="High contrast colours"
          aria-label="High contrast colours"
          aria-pressed={settings.highContrast}
          onClick={(event) => {
            (event.currentTarget as HTMLButtonElement).blur();
            setSettings({ highContrast: !settings.highContrast });
          }}
        >
          ◐
        </button>
      </header>

      <section class="wd-controls" aria-label="Game settings">
        <Choice
          label="Words"
          options={BOARD_COUNTS}
          value={boards}
          name={(option) => `${option}`}
          title={(option) => BOARD_LABEL[option]}
          onPick={pickBoards}
        />
        <Choice
          label="Letters"
          options={WORD_LENGTHS}
          value={length}
          name={(option) => `${option}`}
          title={(option) => `${option}-letter words`}
          onPick={pickLength}
        />
      </section>

      {/* Columns and rows are the two bits of layout the stylesheet cannot know:
          they are the word length and the guess allowance, and both change under
          the player's thumb. */}
      <div
        class="wd-boards"
        data-boards={game.boards}
        style={`--wd-cols:${game.length};--wd-rows:${totalGuesses(game)}`}
      >
        {game.answers.map((_, board) => (
          <Board
            key={board}
            game={game}
            board={board}
            typed={typed}
            playing={playing}
            shake={shake}
            justPlayed={justPlayed}
          />
        ))}
      </div>

      {note && (
        <p class="wd-note" role="alert">
          {note}
        </p>
      )}

      {/* One or the other, in the same place at the foot of the screen. The
          keyboard is an input device with nothing left to input once the game is
          over, and the boards above already show what every letter turned out to
          be — so the finished game gets the space, and the stats with it. */}
      {playing ? (
        <Keyboard game={game} onLetter={addLetter} onEnter={submit} onBack={backspace} />
      ) : (
        <div class={`wd-over${justPlayed !== null ? ' wd-over-new' : ''}`} role="status">
          <StatsRow stat={stat} boards={game.boards} length={game.length} />
          <div class="wd-over-buttons">
            <button type="button" class="wd-primary" onClick={() => newGame(boards, length)}>
              Play again
            </button>
            <button type="button" onClick={share}>
              {copied ? 'Copied ✓' : 'Copy result'}
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
